import type { ContentData } from '@/types/content';

const contentEn: ContentData = {
  common: {
    brandName: "ElhamyLabs",
    fullBrandName: "Elhamy Lab Medical Laboratories",
    fullBrandNameEn: "Elhamy Lab Medical Laboratories",
    home: "Home",
    about: "About Us",
    services: "Services",
    tests: "Test Database",
    branches: "Branches",
    blog: "Blog",
    careers: "Careers",
    contact: "Contact Us",
    book: "Book Now",
    learnMore: "Learn More",
    readMore: "Read More",
    applyNow: "Apply Now",
    sendMessage: "Send Message",
    getDirections: "Get Directions",
    back: "Back",
    search: "Search",
    searchPlaceholder: "Search...",
    noResults: "No results found",
    all: "All",
    email: "Email",
    phone: "Phone",
    address: "Address",
    name: "Name",
    yourName: "Your Name",
    yourEmail: "Your Email",
    yourPhone: "Your Phone",
    yourMessage: "Your Message",
    submit: "Submit",
    virtualTour: "Virtual Tour",
    technology: "Technology & Equipment",
    medicalProfiles: "Medical Profiles",
    preparation: "Preparation",
    turnaround: "Turnaround",
    includes: "Includes",
    price: "Price",
    category: "Category"
  },
  home: {
    hero: {
      title: "ElhamyLabs Medical Laboratory",
      subtitle: "Precision. Trust. Care.",
      description: "We provide advanced medical analysis services with the highest standards of quality and accuracy. We are committed to delivering the best healthcare to our clients through the latest and most advanced technologies in medical laboratory testing.",
      cta: "Book Your Appointment Now"
    },
    services: {
      title: "Our Distinguished Services",
      subtitle: "We offer a comprehensive range of advanced medical laboratory services tailored to all your health needs",
      bloodTests: "Blood Tests",
      hormonePanels: "Hormone Panels",
      allergyTests: "Allergy Tests",
      diabeticProfiles: "Diabetic Profiles",
      childrenLab: "Children Lab",
      womensHealth: "Women's Health",
      corporateSolutions: "Corporate Solutions",
      homeVisits: "Home Visits"
    },
    why: {
      title: "Why ElhamyLabs?",
      subtitle: "We excel in accuracy, speed, and quality in everything we offer",
      accuracy: {
        title: "Accuracy",
        description: "Highest precision in medical analysis"
      },
      speed: {
        title: "Fast Turnaround",
        description: "Quick results without compromising quality"
      },
      certified: {
        title: "Certified",
        description: "Internationally recognized certifications"
      },
      equipment: {
        title: "Advanced Equipment",
        description: "State-of-the-art medical devices and technologies"
      },
      team: {
        title: "Expert Team",
        description: "Experienced medical professionals and technicians"
      },
      branches: {
        title: "Multiple Branches",
        description: "Convenient locations across the city"
      }
    },
    statistics: {
      title: "Our Outstanding Statistics",
      subtitle: "Numbers that speak for themselves and confirm the quality of our services",
      patientsServed: "Patients Served",
      testsAvailable: "Tests Available",
      branchesCount: "Branches",
      accuracyRate: "Accuracy Rate"
    },
    appPromo: {
      title: "Download Our Smart App",
      description: "Book appointments, view results, and manage your health records on the go. A complete health experience in your pocket.",
      appStore: "App Store",
      googlePlay: "Google Play"
    },
    testimonials: {
      title: "What Our Clients Say",
      patient: "Patient",
      items: [
        {
          id: 1,
          name: "Ahmed Mohamed",
          content: "Excellent service and very accurate results. Highly recommended!",
          rating: 5
        },
        {
          id: 2,
          name: "Fatima Ali",
          content: "Professional staff and quick turnaround time. Very satisfied!",
          rating: 5
        },
        {
          id: 3,
          name: "Mohamed Hassan",
          content: "Best laboratory in the city. Accurate results and great service.",
          rating: 5
        }
      ]
    },
    partners: {
      title: "Our Partners & Insurance"
    },
    visitorCounter: {
      text: "You are visitor number {count}"
    }
  },
  about: {
    title: "About Us",
    subtitle: "Leading medical laboratory services with precision, care, and innovation",
    experience: "Over 30 years of experience",
    specialization: "Medical laboratories, blood diseases, and immunology",
    mission: {
      title: "Our Mission",
      description: "To provide accurate, reliable, and timely medical laboratory services that contribute to better healthcare outcomes for our patients."
    },
    vision: {
      title: "Our Vision",
      description: "To be the leading medical laboratory in the region, recognized for excellence, innovation, and patient care."
    },
    journey: {
      title: "Our Journey",
      founded: {
        year: "2010",
        title: "Founded",
        description: "Started with a vision to provide quality medical laboratory services"
      },
      expansion: {
        year: "2015",
        title: "Expansion",
        description: "Opened multiple branches across the city"
      },
      digital: {
        year: "2020",
        title: "Digital Transformation",
        description: "Launched mobile app and online services"
      },
      innovation: {
        year: "2024",
        title: "Innovation",
        description: "Leading the industry with advanced technology"
      },
      rewrite: {
        year: "2025",
        title: "Brand Rewrite",
        titleEn: "Brand Rewrite",
        description: "We refreshed our brand story and digital touchpoints to deliver a clearer, warmer experience for our patients.",
        descriptionEn: "We refreshed our brand story and digital touchpoints to deliver a clearer, warmer experience for our patients."
      }
    },
    values: {
      title: "Our Values",
      excellence: {
        title: "Excellence",
        description: "We strive for excellence in every test we perform"
      },
      accuracy: {
        title: "Accuracy",
        description: "Precision and accuracy are our top priorities"
      },
      transparency: {
        title: "Transparency",
        description: "Clear communication and honest results"
      },
      care: {
        title: "Care",
        description: "Patient care is at the heart of everything we do"
      }
    }
  },
  services: {
    title: "Services",
    subtitle: "Comprehensive medical laboratory services tailored to your needs",
    backToServices: "Back to Services",
    aboutService: {
      title: "About This Service",
      description: "Our {serviceName} service provides comprehensive testing and analysis using the latest medical technology and equipment. We ensure accuracy, reliability, and timely results for all our patients.",
      description2: "Our experienced medical professionals work diligently to provide you with detailed reports and clear explanations of your results."
    },
    keyFeatures: {
      title: "Key Features",
      accurate: "Accurate and reliable results",
      fast: "Fast turnaround time",
      professional: "Professional medical staff",
      equipment: "State-of-the-art equipment",
      reporting: "Comprehensive reporting"
    },
    "blood-tests": {
      name: "Blood Tests",
      shortDescription: "Comprehensive and accurate blood tests",
      heroDescription: "We offer a comprehensive range of blood tests using the latest technologies and advanced medical equipment to ensure accuracy and reliability of results.",
      aboutTitle: "About This Service",
      aboutDescription: "Our blood tests service provides comprehensive testing and analysis using the latest medical technology and equipment. We ensure accuracy, reliability, and timely results for all our patients. Our experienced medical professionals work diligently to provide you with detailed reports and clear explanations of your results.",
      aboutDescription2: "We offer a wide range of blood tests including complete blood count, liver and kidney functions, lipids, glucose, and many other tests. All tests are performed in a sterile and certified environment according to the highest international medical standards.",
      keyFeatures: {
        title: "Key Features",
        items: [
          "Accurate and reliable results",
          "Fast turnaround time",
          "Professional medical staff",
          "State-of-the-art equipment",
          "Comprehensive reporting"
        ]
      }
    },
    "hormone-panels": {
      name: "Hormone Panels",
      shortDescription: "Comprehensive hormone testing",
      heroDescription: "Specialized hormone testing services for comprehensive hormonal health assessment using the latest diagnostic technologies.",
      aboutTitle: "About This Service",
      aboutDescription: "Our hormone panels service provides comprehensive analysis of hormonal functions in the body. We use the latest technologies and medical equipment to ensure accuracy and reliability of results. We ensure accuracy, reliability, and timely results for all our patients.",
      aboutDescription2: "Our experienced medical professionals work diligently to provide you with detailed reports and clear explanations of your results. We offer comprehensive hormone tests including thyroid hormones, sex hormones, growth hormones, and many other important hormones.",
      keyFeatures: {
        title: "Key Features",
        items: [
          "Comprehensive hormone testing",
          "Accurate and reliable results",
          "Detailed medical interpretation",
          "Advanced equipment",
          "Comprehensive reporting"
        ]
      }
    },
    "allergy-tests": {
      name: "Allergy Tests",
      shortDescription: "Advanced allergy testing",
      heroDescription: "Advanced allergy testing services to accurately identify allergens using the latest diagnostic technologies.",
      aboutTitle: "About This Service",
      aboutDescription: "Our allergy tests service provides comprehensive testing and analysis to identify allergens using the latest medical technology and equipment. We ensure accuracy, reliability, and timely results for all our patients.",
      aboutDescription2: "Our experienced medical professionals work diligently to provide you with detailed reports and clear explanations of your results. We offer comprehensive allergy tests including food allergies, environmental allergies, drug allergies, and many other types of allergies.",
      keyFeatures: {
        title: "Key Features",
        items: [
          "Comprehensive allergy testing",
          "Accurate and reliable results",
          "Precise allergen identification",
          "Modern equipment",
          "Treatment recommendations"
        ]
      }
    },
    "diabetic-profiles": {
      name: "Diabetic Profiles",
      shortDescription: "Comprehensive diabetic testing",
      heroDescription: "Comprehensive testing programs and accurate monitoring for diabetic patients using the latest diagnostic technologies.",
      aboutTitle: "About This Service",
      aboutDescription: "Our diabetic profiles service provides comprehensive testing and analysis for diabetic patients using the latest medical technology and equipment. We ensure accuracy, reliability, and timely results for all our patients.",
      aboutDescription2: "Our experienced medical professionals work diligently to provide you with detailed reports and clear explanations of your results. We offer comprehensive tests including fasting glucose, HbA1c, lipids, kidney functions, and other important tests for diabetic patients.",
      keyFeatures: {
        title: "Key Features",
        items: [
          "Comprehensive diabetic testing",
          "Regular monitoring",
          "Accurate and reliable results",
          "Detailed medical interpretation",
          "Treatment recommendations"
        ]
      }
    },
    "children-lab": {
      name: "Children Lab",
      shortDescription: "Specialized laboratory services for children",
      heroDescription: "Specialized and safe laboratory services for children with a comfortable environment and medical staff specially trained to work with children.",
      aboutTitle: "About This Service",
      aboutDescription: "Our children lab service provides specialized and safe laboratory services for children using the latest medical technology and equipment. We ensure accuracy, reliability, and timely results for all our young patients.",
      aboutDescription2: "Our experienced medical professionals work diligently to provide you with detailed reports and clear explanations of your results. We provide a comfortable and safe environment for children with medical staff specially trained to work with children in a gentle and comfortable manner.",
      keyFeatures: {
        title: "Key Features",
        items: [
          "Child-friendly environment",
          "Specialized medical staff",
          "Accurate and reliable results",
          "Safe techniques",
          "Comprehensive reporting"
        ]
      }
    },
    "womens-health": {
      name: "Women's Health",
      shortDescription: "Specialized health tests for women",
      heroDescription: "Specialized health testing services for women with focus on reproductive and hormonal health using the latest technologies.",
      aboutTitle: "About This Service",
      aboutDescription: "Our women's health service provides comprehensive and specialized testing and analysis for women using the latest medical technology and equipment. We ensure accuracy, reliability, and timely results for all our female patients.",
      aboutDescription2: "Our experienced medical professionals work diligently to provide you with detailed reports and clear explanations of your results. We offer comprehensive tests including female hormone tests, pregnancy tests, thyroid tests, and many other important tests for women's health.",
      keyFeatures: {
        title: "Key Features",
        items: [
          "Specialized women's testing",
          "Accurate and reliable results",
          "Complete privacy",
          "Female medical staff",
          "Comprehensive reporting"
        ]
      }
    },
    "corporate-solutions": {
      name: "Corporate Solutions",
      shortDescription: "Laboratory services for corporations",
      heroDescription: "Comprehensive laboratory solutions for companies and institutions with customized health programs and integrated services.",
      aboutTitle: "About This Service",
      aboutDescription: "Our corporate solutions service provides comprehensive laboratory services for companies and institutions using the latest medical technology and equipment. We ensure accuracy, reliability, and timely results for all your employees.",
      aboutDescription2: "Our experienced medical professionals work diligently to provide you with detailed reports and clear explanations of results. We offer customized health programs for companies including regular checkups, pre-employment tests, occupational safety tests, and many other services.",
      keyFeatures: {
        title: "Key Features",
        items: [
          "Customized health programs",
          "Integrated services",
          "Accurate and reliable results",
          "Administrative reports",
          "Competitive pricing"
        ]
      }
    },
    "home-visits": {
      name: "Home Visits",
      shortDescription: "Home sample collection service",
      heroDescription: "Home sample collection service with professional and reliable medical staff for your comfort and easy access.",
      aboutTitle: "About This Service",
      aboutDescription: "Our home visits service provides home sample collection using the latest medical technology and equipment. We ensure accuracy, reliability, and timely results for all our patients.",
      aboutDescription2: "Our experienced medical professionals work diligently to provide you with detailed reports and clear explanations of your results. We provide a comfortable and safe service with professional and trained medical staff who visit you at home at the scheduled time to collect samples.",
      keyFeatures: {
        title: "Key Features",
        items: [
          "Comfortable home service",
          "Professional medical staff",
          "Accurate and reliable results",
          "Easy booking",
          "Results delivery"
        ]
      }
    }
  },
  tests: {
    title: "Test Database",
    subtitle: "Search our comprehensive database of medical tests",
    searchPlaceholder: "Search tests...",
    noTestsFound: "No tests found matching your criteria",
    categories: {
      all: "All",
      bloodTests: "Blood Tests",
      hormoneTests: "Hormone Tests",
      allergyTests: "Allergy Tests",
      other: "Other"
    }
  },
  branches: {
    title: "Branches",
    subtitle: "Find our branches near you",
    whatsapp: "WhatsApp",
    hours: "Working Hours"
  },
  blog: {
    title: "Blog",
    subtitle: "Health insights, medical news, and educational content",
    searchPlaceholder: "Search blog posts...",
    filterByCategory: "Filter by Category",
    allCategories: "All Categories",
    noPostsFound: "No posts found",
    author: "Author",
    publishedOn: "Published on",
    readTime: "min read",
    relatedPosts: "Related Posts",
    shareCta: "To know more just visit",
    categories: {
      healthEducation: "Health Education",
      preventiveCare: "Preventive Care",
      chronicDisease: "Chronic Disease",
      womensHealth: "Women's Health"
    },
    posts: [
      {
        id: 1,
        title: "Understanding Blood Test Results",
        excerpt: "Learn how to read and understand your blood test results and what they mean for your health. A comprehensive guide to help you understand your medical reports.",
        author: "Dr. Ahmed Mohamed",
        date: "2024-01-15",
        category: "healthEducation",
        readTime: 5,
        image: "/assets/images/red-blood-cells.jpeg",
        content: [
          "Blood tests are one of the most common and important medical examinations. These tests help diagnose many health conditions and monitor treatment effectiveness. In this article, we will learn how to read and understand blood test results correctly.",
          "A Complete Blood Count (CBC) is one of the most common tests. These tests measure the number of red blood cells, white blood cells, and platelets. Normal levels indicate good health, while abnormal levels may indicate conditions such as anemia or infection.",
          "Liver and kidney functions are also an important part of blood tests. These tests help assess the health of these vital organs. Elevated levels may indicate problems with the liver or kidneys.",
          "It's important to remember that test results should always be interpreted by a qualified physician. Don't try to diagnose yourself based on results alone. Always consult your doctor for proper interpretation and an appropriate treatment plan."
        ]
      },
      {
        id: 2,
        title: "The Importance of Regular Health Checkups",
        excerpt: "Regular health checkups can help detect health issues early and improve treatment outcomes. Discover when and how you should get checked.",
        author: "Dr. Fatima Ali",
        date: "2024-01-10",
        category: "preventiveCare",
        readTime: 7,
        image: "/assets/images/freepik__talk.jpeg",
        content: [
          "Regular health checkups are the key to maintaining your health and preventing diseases. These checkups help detect health problems in their early stages, when treatment is most effective.",
          "It's recommended to have a comprehensive health checkup at least once a year for adults. This should include blood pressure measurement, blood tests, and other examinations based on age, gender, and medical history.",
          "For people over 40, more frequent checkups are recommended. Special screenings for women and men should also be performed according to medical recommendations.",
          "Remember that prevention is better than cure. Regular checkups don't cost much compared to treating advanced diseases. Invest in your health by getting regular checkups."
        ]
      },
      {
        id: 3,
        title: "Diabetes Management Through Lab Tests",
        excerpt: "How regular lab tests can help manage diabetes and prevent complications. A comprehensive guide for diabetic patients.",
        author: "Dr. Mohamed Hassan",
        date: "2024-01-05",
        category: "chronicDisease",
        readTime: 6,
        image: "/assets/images/freepik__upload.jpeg",
        content: [
          "Diabetes is a chronic condition that requires continuous monitoring and careful management. Regular lab tests are an essential part of a successful diabetes management plan.",
          "Fasting Blood Sugar (FBS) and Hemoglobin A1c (HbA1c) tests are among the most important tests for diabetic patients. HbA1c helps assess average blood sugar levels over the past 2-3 months.",
          "It's also important to regularly monitor kidney function and lipids, as diabetic patients are at increased risk of kidney problems and heart disease. These tests should be performed every 3-6 months according to your doctor's recommendations.",
          "Adhering to a regular testing schedule helps detect any problems early and take appropriate action. Remember that successful diabetes management requires close cooperation with your healthcare team."
        ]
      },
      {
        id: 4,
        title: "Women's Health: Essential Tests",
        excerpt: "Important laboratory tests every woman should consider for optimal health. A comprehensive guide to women's health.",
        author: "Dr. Sara Ibrahim",
        date: "2024-01-01",
        category: "womensHealth",
        readTime: 8,
        image: "/assets/images/3d-cartoon-style-character.jpeg",
        content: [
          "Women's health requires special attention and regular screenings to ensure overall well-being. There are many important tests that every woman should consider at different stages of her life.",
          "Female hormone tests are particularly important for women of reproductive age. These tests help assess reproductive health and diagnose any hormonal issues that may affect fertility or overall health.",
          "Thyroid function tests are also important for women, as thyroid disorders are more common in women. This test should be performed regularly, especially if there are symptoms such as fatigue or weight changes.",
          "Vitamin D and calcium tests are important for bone health, especially with advancing age. Women are particularly susceptible to osteoporosis, so it's important to monitor levels of these vitamins and minerals.",
          "Remember that prevention and regular screenings are the key to maintaining your health. Consult your doctor to determine the appropriate screening schedule for your age and medical history."
        ]
      },
      {
        id: 5,
        title: "What Do Cholesterol Results Mean?",
        excerpt: "A comprehensive guide to understanding cholesterol and lipid test results, and how to maintain healthy levels through diet and lifestyle.",
        author: "Dr. Khaled Mahmoud",
        date: "2023-12-28",
        category: "healthEducation",
        readTime: 6,
        image: "/assets/images/blood-infographic.jpeg",
        content: [
          "Cholesterol is a fatty substance found in the blood that is essential for body health. However, high cholesterol levels can increase the risk of cardiovascular disease. Understanding your cholesterol test results helps you make informed health decisions.",
          "A complete lipid profile measures several types of fats in the blood: Total Cholesterol, Low-Density Lipoprotein (LDL or 'bad' cholesterol), High-Density Lipoprotein (HDL or 'good' cholesterol), and Triglycerides. Each type plays an important role in heart health.",
          "LDL cholesterol should be less than 100 mg/dL for healthy individuals, and less than 70 mg/dL for those at risk of heart disease. HDL cholesterol should be above 40 mg/dL for men and 50 mg/dL for women. Higher HDL levels are actually beneficial as they help remove cholesterol from the arteries.",
          "You can improve your cholesterol levels through: eating a healthy diet rich in fruits, vegetables, and whole grains, regular exercise, maintaining a healthy weight, and avoiding smoking. In some cases, your doctor may recommend medications to lower cholesterol.",
          "It's important to have your cholesterol checked regularly, especially if you have a family history of heart disease or other risk factors. Consult your doctor to determine the appropriate testing schedule for you."
        ]
      },
      {
        id: 6,
        title: "Liver and Kidney Function Tests: A Complete Guide",
        excerpt: "Learn about the importance of liver and kidney function tests, when to get them, and how to interpret results to maintain the health of these vital organs.",
        author: "Dr. Nora Ahmed",
        date: "2023-12-25",
        category: "preventiveCare",
        readTime: 7,
        image: "/assets/images/human.png",
        content: [
          "The liver and kidneys are among the most important organs in the body, playing vital roles in detoxification, fluid regulation, and production of important proteins. Function tests for these organs help detect problems early before they become serious.",
          "Liver function tests measure levels of liver enzymes such as ALT and AST, bilirubin, and albumin. Elevated levels of these enzymes may indicate hepatitis, liver damage, or other problems. It's important to note that certain medications and alcohol can affect the results of these tests.",
          "Kidney function tests measure levels of creatinine and urea in the blood. Creatinine is a waste product produced by muscles and filtered from the blood by the kidneys. Elevated levels may indicate kidney function problems. The test also measures the estimated Glomerular Filtration Rate (eGFR), which provides an accurate indicator of kidney efficiency.",
          "It's recommended to have liver and kidney function tests: as part of an annual health checkup, before starting new medications that may affect these organs, if you have a family history of liver or kidney disease, or if you experience symptoms such as fatigue, yellowing of the skin, or swelling in the legs.",
          "Maintaining liver and kidney health requires: drinking adequate amounts of water, following a healthy diet, avoiding excessive alcohol consumption, regular exercise, and avoiding medications that may harm these organs except under medical supervision."
        ]
      },
      {
        id: 7,
        title: "Preparing for Lab Tests: Important Tips",
        excerpt: "Learn how to properly prepare for medical tests to ensure accurate results. A comprehensive guide to pre-test preparation.",
        author: "Dr. Youssef Abdullah",
        date: "2023-12-20",
        category: "healthEducation",
        readTime: 5,
        image: "/assets/images/pexels.png",
        content: [
          "Proper preparation for medical tests is crucial for obtaining accurate and reliable results. Inaccurate results may lead to misdiagnosis or inappropriate treatment. Following medical instructions precisely ensures the best outcomes.",
          "For tests that require fasting (such as fasting blood sugar, cholesterol, and liver function tests): You must fast for 8-12 hours before the test. During the fasting period, you can drink water only, but must avoid food, sweetened beverages, coffee, and tea. If you're taking medications, consult your doctor about whether you should take them before the test.",
          "For general tests: You can eat and drink normally, but it's recommended to avoid heavy meals immediately before the test. Wear loose clothing to facilitate sample collection, especially from the arm. Inform the technician if you feel anxious or dizzy.",
          "Before the test: Get adequate sleep, avoid strenuous exercise 24 hours before the test, avoid alcohol 24 hours before the test, and inform your doctor about all medications and supplements you're taking.",
          "If you forgot the instructions or didn't fully understand them, don't hesitate to contact the lab or your doctor for clarification. Proper preparation saves time and money and ensures accurate results that help in correct diagnosis and treatment."
        ]
      },
      {
        id: 8,
        title: "Hormone Testing: When and Why?",
        excerpt: "A comprehensive guide to understanding the importance of hormone tests, when to get them, and how they can help diagnose various health issues.",
        author: "Dr. Mariam Said",
        date: "2023-12-15",
        category: "womensHealth",
        readTime: 6,
        image: "/assets/images/sex-education.png",
        content: [
          "Hormones are chemical messengers that travel through the blood to regulate various body functions. Any imbalance in hormone levels can affect overall health, mood, energy, and reproductive functions. Hormone tests help identify and treat problems.",
          "For women, hormone tests are particularly important in cases of: irregular menstrual cycles, difficulty conceiving, menopausal symptoms, skin and hair problems, mood changes, or unexplained weight gain. The main female hormones tested include estrogen, progesterone, FSH hormone, and LH hormone.",
          "Thyroid hormones (TSH, T3, T4) are important for everyone. Thyroid dysfunction is very common and can cause symptoms such as fatigue, weight gain or loss, hair loss, and mood changes. It's recommended to test thyroid function as part of routine health screening.",
          "Cortisol hormone (stress hormone) can be tested if you suffer from chronic fatigue, sleep difficulties, or other stress symptoms. Growth hormones and insulin may be tested in specific cases based on symptoms and medical history.",
          "Hormone tests should be performed at a specific time in the menstrual cycle for women (usually on day 3 of the cycle). Consult your doctor about the best time to perform the test. Normal results vary by age, gender, and life stage, so it's important for the doctor to interpret the results in the context of your overall health condition."
        ]
      },
      {
        id: 9,
        title: "Allergy Testing: Types and Importance",
        excerpt: "Learn about different types of allergy tests, when to get them, and how they can help identify allergens that affect your health.",
        author: "Dr. Amr Ismail",
        date: "2023-12-10",
        category: "preventiveCare",
        readTime: 5,
        image: "/assets/images/coronavirus.png",
        content: [
          "Allergies are an overreaction of the immune system to normal, harmless substances. Allergies can cause annoying symptoms such as sneezing, itching, skin rashes, or in severe cases, difficulty breathing. Allergy tests help identify the substances causing allergies (allergens) so they can be avoided or treated.",
          "There are several types of allergy tests: Skin Prick Test where small drops of allergens are placed on the skin and the reaction is monitored, Blood Test (RAST or ImmunoCAP) that measures IgE antibody levels in the blood, and Food Challenge Test performed under medical supervision.",
          "Blood allergy testing is particularly useful for people who: have skin conditions that make skin testing difficult, are taking medications that may interfere with skin test results, or have a history of severe allergic reactions. This type of testing is safe and can be performed at any time.",
          "It's important to get allergy testing if you experience: persistent symptoms such as sneezing, runny nose, or itchy eyes, skin rashes or eczema, digestive symptoms after eating certain foods, or difficulty breathing after exposure to certain substances. Identifying allergens helps avoid them and reduce symptoms.",
          "After identifying allergens, you can: avoid the allergenic substances, use anti-allergy medications when needed, or consider immunotherapy in severe cases. Consult an allergist to determine the best treatment plan for you."
        ]
      },
      {
        id: 10,
        title: "Managing High Blood Pressure Through Lab Tests",
        excerpt: "How regular medical tests can help monitor and manage high blood pressure, preventing serious complications.",
        author: "Dr. Hala Mohsen",
        date: "2023-12-05",
        category: "chronicDisease",
        readTime: 7,
        image: "/assets/images/realistic-world.png",
        content: [
          "High blood pressure (Hypertension) is a common condition affecting millions of people worldwide. If left untreated, it can lead to serious health problems such as heart disease, stroke, and kidney disease. Regular medical tests are an essential part of successfully managing high blood pressure.",
          "When high blood pressure is diagnosed, doctors usually recommend a series of tests to assess overall health and identify any potential complications. These tests include: urinalysis to check for protein or blood, complete blood count, kidney function tests, liver function tests, cholesterol and lipid tests, and blood sugar tests.",
          "Kidney function testing is particularly important for patients with high blood pressure, as high blood pressure can cause kidney damage, and damaged kidneys can increase blood pressure. Creatinine and urea levels in the blood are tested, along with the estimated Glomerular Filtration Rate (eGFR) to assess kidney health.",
          "Cardiovascular examination may also include an electrocardiogram (ECG) and eye examination to check for any changes in blood vessels. These tests help detect any damage that may have already occurred due to high blood pressure.",
          "After starting treatment, regular tests should be performed every 3-6 months to monitor: medication effectiveness, any potential side effects, kidney and heart health, and blood lipid and sugar levels. Adhering to a regular testing schedule helps ensure that treatment is working correctly and that your overall health is protected.",
          "In addition to medical tests, it's also important to: measure blood pressure regularly at home, follow a healthy low-salt diet, exercise regularly, maintain a healthy weight, avoid smoking, and reduce stress. These lifestyle changes, along with regular tests, help successfully manage high blood pressure."
        ]
      }
    ]
  },
  careers: {
    title: "Careers",
    subtitle: "Join our team and make a difference in healthcare",
    openPositions: "Open Positions",
    department: "Department",
    location: "Location",
    type: "Type",
    fullTime: "Full-time",
    partTime: "Part-time"
  },
  contact: {
    title: "Contact Us",
    subtitle: "Get in touch with us. We're here to help.",
    contactInfo: {
      title: "Contact Information",
      phone: "Phone",
      phone1: "010 0607 6325",
      phone2: "040 221 2255",
      phone3: "040 221 3030",
      email: "Email",
      address: "Address",
      fullAddress: "23 July Street (formerly Sea Street), Barakat Building, next to El-Mahalla Cinema, El-Mahalla El-Kubra City, Gharbia Governorate, Egypt"
    },
    sendMessage: {
      title: "Send us a Message",
      namePlaceholder: "Your Name", 
      messagePlaceholder: "Your Message",
      successMessage: "Thank you for your message! We will get back to you soon."
    }
  },
  virtualTour: {
    title: "Virtual Lab Tour",
    subtitle: "Take a virtual tour of our state-of-the-art facilities",
    reception: {
      title: "Reception Area",
      description: "Welcome to our modern reception area"
    },
    laboratory: {
      title: "Laboratory",
      description: "State-of-the-art laboratory facilities"
    },
    sampleCollection: {
      title: "Sample Collection",
      description: "Comfortable sample collection rooms"
    },
    analysisRoom: {
      title: "Analysis Room",
      description: "Advanced analysis and testing equipment"
    },
    waitingArea: {
      title: "Waiting Area",
      description: "Comfortable waiting areas for patients"
    },
    qualityControl: {
      title: "Quality Control",
      description: "Quality control and assurance department"
    }
  },
  technology: {
    title: "Technology & Equipment",
    subtitle: "Cutting-edge technology for accurate and reliable results",
    automatedAnalyzer: {
      title: "Automated Analyzer",
      description: "State-of-the-art automated blood analysis system for accurate and fast results"
    },
    digitalImaging: {
      title: "Digital Imaging",
      description: "Advanced digital imaging technology for precise sample analysis"
    },
    qualityControl: {
      title: "Quality Control Systems",
      description: "Comprehensive quality control systems ensuring accuracy and reliability"
    },
    highSpeed: {
      title: "High-Speed Processing",
      description: "High-speed processing units for rapid test results without compromising quality"
    },
    workflow: {
      title: "Our Workflow",
      sampleCollection: {
        title: "Sample Collection",
        description: "Professional sample collection"
      },
      processing: {
        title: "Processing",
        description: "Automated processing and analysis"
      },
      qualityControl: {
        title: "Quality Control",
        description: "Rigorous quality checks"
      },
      results: {
        title: "Results",
        description: "Accurate and timely results"
      }
    }
  },
  medicalProfiles: {
    title: "Medical Profiles",
    subtitle: "Comprehensive health screening packages tailored to your needs",
    searchPlaceholder: "Search profiles...",
    completeHealth: {
      title: "Complete Health Checkup",
      description: "Comprehensive health assessment including blood tests, liver function, and kidney function",
      tests: ["CBC", "Liver Function", "Kidney Function", "Lipid Profile"]
    },
    diabetes: {
      title: "Diabetes Profile",
      description: "Complete diabetes monitoring and management profile",
      tests: ["Fasting Blood Sugar", "HbA1c", "Lipid Profile", "Kidney Function"]
    },
    cardiac: {
      title: "Cardiac Profile",
      description: "Heart health assessment and cardiovascular risk evaluation",
      tests: ["Lipid Profile", "Cardiac Enzymes", "CBC", "Kidney Function"]
    },
    womensHealth: {
      title: "Women's Health Profile",
      description: "Comprehensive women's health screening",
      tests: ["Hormone Panel", "Thyroid Function", "CBC", "Vitamin D"]
    },
    preparation: {
      fasting12: "Fasting 12 hours",
      noPreparation: "No special preparation"
    }
  },
  footer: {
    copyright: "All rights reserved",
    builtBy: "This system was designed & built by the",
    email: "Email",
    phone: "Phone",
    phone1: "010 0607 6325",
    phone2: "040 221 2255",
    phone3: "040 221 3030",
    address: "23 July Street (formerly Sea Street), Barakat Building, next to El-Mahalla Cinema, El-Mahalla El-Kubra City, Gharbia Governorate, Egypt"
  },
  homeVisitRequest: {
    title: "Home Visit Request",
    subtitle: "Fill out the form below to request a home visit from our medical team",
    form: {
      name: "Name",
      namePlaceholder: "Enter your full name",
      code: "Code (if exists)",
      codePlaceholder: "Enter code if exists",
      phone1: "Phone Number 1",
      phone2: "Phone Number 2 (if exists)",
      phonePlaceholder: "Enter phone number",
      age: "Age",
      agePlaceholder: "Select age",
      tests: "Write Required Tests",
      testsPlaceholder: "Write a list of required tests",
      prescription: "Upload Prescription Image",
      uploadPrescription: "Click to upload prescription image",
      preferredDate: "Preferred Date",
      preferredTime: "Preferred Time",
      address: "Address in Details",
      addressPlaceholder: "Enter address in details",
      submit: "Submit Request",
      submitting: "Submitting..."
    },
    errors: {
      nameRequired: "Name is required",
      phoneRequired: "Phone number is required",
      phoneInvalid: "Invalid phone number format",
      ageRequired: "Age is required",
      addressRequired: "Address is required"
    },
    successMessage: "Your request has been submitted successfully! We will contact you soon."
  }
};

export default contentEn;

